import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CustomImageModal, getOrdinal } from "../CustomFunctions";
import UserDefaultLogo from "../UserDetails/UserDefaultLogo";
import { BASE_URL, IMAGE_URL } from "../../config/axios";
import { CircularGrid, StarPerformerIcon } from "../AllSvg";
import Loader from "../Loader";

const getCurrentMonthYear = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};

const StarIcon = ({ fillPercentage, id }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id={`grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset={`${fillPercentage}%`} stopColor="#FFC107" />
                <stop offset={`${fillPercentage}%`} stopColor="#E0E0E0" />
            </linearGradient>
        </defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill={`url(#grad-${id})`} stroke="#FFC107" strokeWidth="1" />
    </svg>
);

const TopPerformersWidget = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [topEmployees, setTopEmployees] = useState([]);
    const [allEmployeesData, setAllEmployeesData] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [departmentLists, setDepartmentLists] = useState([]);
    const [deptFilter, setDeptFilter] = useState("null");
    const [overallTotalMarks, setOverallTotalMarks] = useState(30);

    const currentUrl = window.location.href;
    const urlParts = currentUrl.split('/');
    const url = urlParts[3];

    const deptOptions = departmentLists.map((dept) => ({
        value: String(dept.id),
        label: dept.title
    }));

    const subCompany = sessionStorage.getItem("company_id");
    const currentMonthYear = getCurrentMonthYear();

    // Fetch Departments for local dropdown
    useEffect(() => {
        const fetchDepartments = async () => {
            if (!subCompany || subCompany === "null") return;
            try {
                const res = await axios.get(`${BASE_URL}/wfm/departmentfilterbysubcompanycount/${subCompany}/active/`);
                setDepartmentLists(res.data || []);
            } catch (err) {
                console.error("Failed to load departments", err);
            }
        };
        fetchDepartments();
    }, [subCompany]);

    // Fetch Top 5 Employees & All Data for Distribution
    useEffect(() => {
        const fetchTopPerformers = async () => {
            if (!subCompany || subCompany === "null") return;
            setLoading(true);
            try {
                const params = {
                    sub_company: subCompany,
                    start_month_year: currentMonthYear,
                    end_month_year: currentMonthYear,
                    sort_order: "high_to_low",
                    employee_status: "active"
                };
                if (deptFilter !== "null") params.department_id = deptFilter;

                const res = await axios.get(`${BASE_URL}/wfm/star-performance-overall-rating/`, { params });
                const employees = res.data?.employees || [];
                setTopEmployees(employees.slice(0, 5));
                setAllEmployeesData(employees);
                setDateRange(res.data?.date_range || null);

                // NEW: capture overall total marks for star scaling & distribution
                setOverallTotalMarks(Number(res.data?.overall_total_marks) || 30);
            } catch (err) {
                console.error("Failed to load top performers", err);
            } finally {
                setLoading(false);
            }
        };
        fetchTopPerformers();
    }, [subCompany, deptFilter, currentMonthYear]);

    const renderStars = (finalMarks, maxMarks = overallTotalMarks) => {
        const scoreOutOf5 = Math.min(Number(finalMarks || 0) / (maxMarks / 5), 5);
        return (
            <div style={{ display: "flex", gap: "2px" }} title={`${scoreOutOf5.toFixed(2)} / 5 Stars`}>
                {[1, 2, 3, 4, 5].map((index) => {
                    let fill = 0;
                    if (scoreOutOf5 >= index) fill = 100;
                    else if (scoreOutOf5 > index - 1) fill = (scoreOutOf5 - (index - 1)) * 100;
                    return <StarIcon key={index} id={`${finalMarks}-${index}`} fillPercentage={fill} />;
                })}
            </div>
        );
    };

    // Calculate Distribution
    const totalEmployees = allEmployeesData.length;
    let excellentCount = 0;
    let averageCount = 0;
    let poorCount = 0;

    allEmployeesData.forEach((emp) => {
        const finalMarks = Number(emp.overall_final_marks || 0);
        // Convert to percentage of overall_total_marks, then use the same 85% / 50% thresholds
        const pct = overallTotalMarks > 0 ? (finalMarks / overallTotalMarks) * 100 : 0;
        if (pct >= 85) excellentCount++;
        else if (pct >= 50) averageCount++;
        else poorCount++;
    });

    const excellentWidth = totalEmployees > 0 ? (excellentCount / totalEmployees) * 100 : 0;
    const averageWidth = totalEmployees > 0 ? (averageCount / totalEmployees) * 100 : 0;
    const poorWidth = totalEmployees > 0 ? (poorCount / totalEmployees) * 100 : 0;

    return (
        <div className="db2-card" style={{ display: "flex", flexDirection: "column", width: "44vw", height: "71vh" }}>

            {/* Header matching your db2-card standard */}
            <div className="db2-card-header">

                <div className="db2-card-title" style={{ margin: 0 }}>
                    <div className="db2-card-icon db2-icon-blue">
                        <StarPerformerIcon />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>Top Performers</span>
                        {dateRange?.start_date && dateRange?.end_date && (
                            <div style={{ fontSize: "14px", color: "#64748b", marginTop: "4px", fontWeight: "500" }}>
                                {dateRange.start_date} to {dateRange.end_date}
                            </div>
                        )}
                    </div>
                </div>
                {/* Department Select Filter */}
                <div className="field-cont-div">
                    <CircularGrid />
                    <select
                        className="attendance-input-field width-10vw date-field"
                        value={deptFilter}
                        onChange={(e) => setDeptFilter(e.target.value || "null")}
                    >
                        <option value="null">All Department</option>
                        {deptOptions
                            .slice()
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map((dept) => (
                                <option key={dept.value} value={dept.value}>{dept.label}</option>
                            ))}
                    </select>
                </div>
            </div>

            <div className="db2-divider" style={{ margin: 0 }} />

            <div style={{ padding: "15px 20px", display: "flex", flexDirection: "column", flex: 1 }}>


                {/* Scrollable List area */}
                <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "9px" }}>
                    {loading ? (
                        <div style={{
                            width: "100%",
                            minHeight: "280px",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <Loader container={true} />
                        </div>
                    ) : topEmployees.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#888", fontSize: "13px" }}>No top performers found for this month.</div>
                    ) : (
                        topEmployees.map((emp) => (
                            <div
                                key={emp.empcode}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    paddingBottom: "10px",
                                    borderBottom: "1px solid #f1f5f9",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    {/* <span
                                        style={{
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            color: "#64748b",
                                            minWidth: "22px",
                                        }}
                                    >
                                        #{emp.sub_company_rank}
                                    </span> */}

                                    <CustomImageModal customStyle>
                                        {emp.profilepic ? (
                                            <img
                                                src={`${IMAGE_URL}${emp.profilepic}`}
                                                className="profile-img"
                                                alt={emp?.name}
                                            />
                                        ) : (
                                            <UserDefaultLogo
                                                user={{ name: emp.name, profilepic: "" }}
                                                customStyleforParent={"reqOutDuty-default-logo"}
                                                customStyleforChild={"small-profile"}
                                            />
                                        )}
                                    </CustomImageModal>

                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <span
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#1e293b",
                                                lineHeight: "1.2",
                                            }}
                                        >
                                            {emp.name}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "12px",
                                                color: "#64748b",
                                                whiteSpace: "nowrap",
                                                maxWidth: "140px",
                                            }}
                                        >
                                            {emp.designation_name}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                    {/* <span style={{ fontSize: "14px", fontWeight: "bold", color: "#10b981" }}>
                                        {emp.overall_final_marks}
                                    </span>
                                    {renderStars(emp.overall_final_marks || 0)} */}
                                    <span
                                        style={{
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            color: "#64748b",
                                            minWidth: "22px",
                                        }}
                                    >
                                        {getOrdinal(emp.sub_company_rank)}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Option 3: Performance Distribution */}
            <div style={{ marginTop: 'auto', }}>
                <div style={{ padding: '8px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Workforce Overview</span>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>{totalEmployees} Total Employees</span>
                    </div>

                    {/* Segmented Progress Bar */}
                    <div style={{ display: 'flex', width: '100%', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '10px' }}>
                        <div style={{ width: `${excellentWidth}%`, backgroundColor: '#10b981' }} title={`Excellent (${Math.round(excellentWidth)}%)`}></div>
                        <div style={{ width: `${averageWidth}%`, backgroundColor: '#eab308' }} title={`Average (${Math.round(averageWidth)}%)`}></div>
                        <div style={{ width: `${poorWidth}%`, backgroundColor: '#ef4444' }} title={`Poor (${Math.round(poorWidth)}%)`}></div>
                    </div>

                    {/* Legend */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            fontWeight: "700",
                            color: "#475569",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10b981" }}></div>
                            Excellent (≥85%)
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#eab308" }}></div>
                            Average (50–85%)
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ef4444" }}></div>
                            Poor (&lt;50%)
                        </div>
                    </div>
                </div>
            </div>

            <div className="db2-card-header" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "none", paddingTop: "0px", marginTop: "10px", marginBottom: "0px", }}>

                {/* Redirect Button */}
                <button
                    onClick={() => navigate(`/${url}/${url === 'hr' ? 'hrRequestManagement' : 'adminRequestManagement'}`, { state: url === 'admin' ? { secondtoggle: 9 } : { secondtoggle: 12 } })}
                    style={{ fontSize: "13px", color: "#2576bc", background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}
                >
                    View All →
                </button>
            </div>
        </div>
    );
};

export default TopPerformersWidget;